/**
 * Sidebar 组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/shared/store/authStore'
import { useChatStore } from '../store/chatStore'
import { ProjectApiService } from '@/modules/agent/services/project-api'
import { ChevronDown, FolderOpen, Loader2, AlertCircle, Clock, ArrowLeft } from 'lucide-react'
import type { Session } from '../types/memory'
import { SessionList } from './SessionList'
import { UserSearch } from './UserSearch'

type StatusFilter = 'all' | 'failed' | 'delayed'

interface SidebarProps {
  projectId: string
  onProjectIdChange: (id: string) => void
  onSessionSelect?: (sessionId: string) => void
  currentSessionId?: string
  onReExtract?: (sessionId: string) => void
}

export default function Sidebar({
  projectId,
  onProjectIdChange,
  onSessionSelect,
  currentSessionId,
  onReExtract,
}: SidebarProps) {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { newSession } = useChatStore()

  const [projects, setProjects] = useState<{ id: string; name: string }[]>([{ id: 'default', name: 'Default' }])
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)
  const isPrivilegedUser = user?.role === 'developer' || user?.role === 'admin'
  // 用户主键统一用 id
  const initialUserId = user?.id
  const [viewingUserId, setViewingUserId] = useState<string | undefined>(
    isPrivilegedUser ? undefined : initialUserId
  )
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [statusCounts, setStatusCounts] = useState({ failed: 0, delayed: 0 })
  
  const canReExtract = isPrivilegedUser

  // 会话加载完成时更新状态统计
  const handleSessionsLoaded = useCallback((sessions: Session[]) => {
    const counts = { failed: 0, delayed: 0 }
    sessions.forEach(s => {
      if (s.status === 'failed') counts.failed++
      if (s.status === 'delayed') counts.delayed++
    })
    setStatusCounts(counts)
  }, [])

  // 加载项目列表（从 Agent Admin API 获取）
  const loadProjects = useCallback(async () => {
    const currentUser = useAuthStore.getState().user
    if (!currentUser) return
    setIsLoadingProjects(true)
    try {
      console.log('[Sidebar] 加载项目列表...')
      const response = await ProjectApiService.getProjects()
      console.log('[Sidebar] 项目列表响应:', response)
      
      if (response.data && response.data.length > 0) {
        // 提取项目 ID 和名称列表
        const projectList = response.data.map(project => ({
          id: project.id,
          name: project.name || project.id,
        }))
        console.log('[Sidebar] 项目列表:', projectList)
        setProjects(projectList)
        
        // 如果当前选中的项目不在列表中，切换到第一个项目
        const projectIds = projectList.map(p => p.id)
        if (!projectIds.includes(projectId)) {
          console.log('[Sidebar] 当前项目不在列表中，切换到:', projectList[0].id)
          onProjectIdChange(projectList[0].id)
        }
      } else {
        console.log('[Sidebar] 没有项目，使用默认值')
        setProjects([{ id: 'default', name: 'Default' }])
      }
    } catch (error) {
      console.error('[Sidebar] 加载项目列表失败:', error)
      setProjects([{ id: 'default', name: 'Default' }])
    } finally {
      setIsLoadingProjects(false)
    }
  }, [projectId, onProjectIdChange])

  // 用户主键统一用 id
  const currentUserId = user?.id

  // 初始加载项目列表
  useEffect(() => {
    if (currentUserId) {
      console.log('[Sidebar] useEffect 触发，currentUserId:', currentUserId)
      loadProjects()
    }
  }, [currentUserId, loadProjects])

  // 用户变化时更新 viewingUserId
  useEffect(() => {
    if (currentUserId && !isPrivilegedUser && !viewingUserId) {
      setViewingUserId(currentUserId)
    }
  }, [currentUserId, isPrivilegedUser, viewingUserId])

  // 选择用户
  const handleUserSelect = (userId: string) => {
    console.log('[Sidebar] User selected:', userId, 'previous:', viewingUserId)
    setViewingUserId(userId)
  }

  // 选择会话
  const handleSessionSelect = (sessionId: string) => {
    onSessionSelect?.(sessionId)
  }

  // 新建会话
  const handleNewSession = () => {
    newSession()
  }

  return (
    <aside className="w-80 flex flex-col h-full bg-card border-r border-border shadow-sm">
      {/* Logo + 返回按钮 */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          {/* 返回按钮 */}
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-1 rounded-lg hover:bg-secondary transition-colors"
            title="返回控制台"
          >
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
            🧠
          </div>
          <div>
            <h1 className="font-semibold text-sm text-foreground">
              Memory Service
            </h1>
            <p className="text-xs text-muted-foreground">
              知识图谱记忆中台
            </p>
          </div>
        </div>
      </div>

      {/* 用户搜索 + 项目选择 */}
      <div className="p-4 space-y-3 border-b border-border">
        {/* 用户搜索 */}
        <UserSearch
          onUserSelect={handleUserSelect}
          currentUserId={viewingUserId}
        />

        {/* 状态筛选标签 */}
        {canReExtract && (statusCounts.failed > 0 || statusCounts.delayed > 0) && (
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                statusFilter === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-secondary text-muted-foreground'
              }`}
            >
              全部
            </button>
            {statusCounts.failed > 0 && (
              <button
                onClick={() => setStatusFilter('failed')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  statusFilter === 'failed'
                    ? 'bg-red-500 text-white'
                    : 'bg-red-500/15 text-red-400'
                }`}
              >
                <AlertCircle className="w-3 h-3" />
                失败({statusCounts.failed})
              </button>
            )}
            {statusCounts.delayed > 0 && (
              <button
                onClick={() => setStatusFilter('delayed')}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                  statusFilter === 'delayed'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-yellow-500/15 text-yellow-400'
                }`}
              >
                <Clock className="w-3 h-3" />
                延时({statusCounts.delayed})
              </button>
            )}
          </div>
        )}

        {/* 项目 ID - 下拉选择 */}
        <div>
          <label className="block text-xs mb-1.5 text-muted-foreground">
            项目
          </label>
          <div className="relative">
            <select
              value={projectId}
              onChange={(e) => onProjectIdChange(e.target.value)}
              disabled={isLoadingProjects}
              className="w-full px-3 py-2 rounded-xl text-sm appearance-none cursor-pointer pr-10 bg-secondary border border-border text-foreground outline-none shadow-sm"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1">
              {isLoadingProjects ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <FolderOpen className="w-3.5 h-3.5 text-muted-foreground" />
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 会话列表 */}
      <div className="flex-1 overflow-hidden flex flex-col py-2">
        <SessionList
          userId={viewingUserId || currentUserId}
          projectId={projectId}
          currentSessionId={currentSessionId}
          onSessionSelect={handleSessionSelect}
          onNewSession={handleNewSession}
          onReExtract={onReExtract}
          canReExtract={canReExtract}
          statusFilter={statusFilter}
          onSessionsLoaded={handleSessionsLoaded}
        />
      </div>
    </aside>
  )
}

